import {
    AdPlatform,
    PLAYBOOK_TYPE_INFO,
    PlaybookType,
    PlaybookTypeInfo
} from './Const';

import {
    InstagramPageType,
    FacebookPageType,
    FBPixel,
    AdAccountPageType,
    PlaybookStep,
    RecordDetails,
    ExternalLink,
    PixelConversionEvent,
    TargetedLanguage,
    TargetedGender,
    GENDERS,
    TikTokAdAccount,
    TikTokSparkAd,
    AdConnectionOptions
} from './Types';

import {
    Caption,
    CampaignState,
    CampaignGoal,
    TargetedGeographyBucket,
    CreativeSelectorPost,
    transformPostsToCreativeFormat
} from 'Hooks/useCampaignBuilder';

export const FB_CAMPAIGNS_DEFAULT_LANGUAGES: TargetedLanguage[] = [
    { key: 6, name: 'English (US)' },
    { key: 24, name: 'English (UK)' }
]

export const FB_DEFAULT_GENDERS: TargetedGender[] = [
    {
        key: 1,
        name: GENDERS.MALE
    },
    {
        key: 2,
        name: GENDERS.FEMALE
    }
]

export const FB_DEFAULT_MIN_AGE: number = 18;
export const FB_DEFAULT_MAX_AGE: number = 65;

export interface PromotableItem extends PlaybookTypeInfo {
    isPopular?: boolean;
    onlyForSymphonyBetaTestUsers?: boolean;
    unavailable?: boolean;
}

// ==================== TYPE DEFINITIONS ====================
interface FacebookCampaignConnections {
    selectedFacebookAdAccount?: AdAccountPageType;
    selectedFacebookPixel?: FBPixel;
    selectedFacebookPage?: FacebookPageType;
    selectedInstagramPage?: InstagramPageType;
}

// Date range configuration when specific dates are chosen
interface DateRangeConfig {
    startDate: string;
    endDate: string;
}

// Duration configuration when number of days is chosen
interface DurationConfig {
    startDate: string;
    numberOfDays: number;
}

// Maximum budget configuration for daily budget campaigns
interface MaximumBudgetConfig {
    startDate: string;
    maximumBudget: number;
}

// Type for lifetime budget campaigns
type LifetimeBudgetConfig = (DateRangeConfig & { dateType: 'date_range' }) | (DurationConfig & { dateType: 'duration' });

// Type for daily budget campaigns
type DailyBudgetConfig = (DateRangeConfig & { dateType: 'date_range' })
    | (DurationConfig & { dateType: 'duration' })
    | (MaximumBudgetConfig & { dateType: 'maximum_budget' })

export type CampaignBudgetDateConfig = (LifetimeBudgetConfig | DailyBudgetConfig) & {
    // Number of days the campaign was extended by
    daysExtended?: number;
    // Final start date after any adjustments
    finalStartDate?: string;
    // Final end date after any adjustments
    finalEndDate?: string;
};

export type TargetedAudienceType = 'customAudience' | 'lookalikeAudience' | 'interestTargetingAudience' | 'pageAudience' | 'presetAudience'
    | 'hashtagAudience' | 'interestTargeting_CategoryAudience' | 'interestTargeting_KeywordAudience' | 'userAudience';

export interface TargetedAudience_AudienceItem {
    id: string;
    name?: string;
    url?: string;
    type: TargetedAudienceType;
}

export interface TargetedAudience {
    id: string;
    name: string;
    audiences: Array<TargetedAudience_AudienceItem>;
    exclusionAudiences?: Array<TargetedAudience_AudienceItem>;
}

// Comprehensive type definition for Facebook campaign submissions
export type FacebookCampaignSubmissionType = {
    // Type of playbook being used for the campaign
    playbookType: PlaybookType;

    // Music record details for streaming campaigns
    record: RecordDetails | null;

    // For Grow Playlist followers campaigns, this is the track that is being targeted
    highlightedTrack?: {
        id: string,
        uri: string,
        name: string,
    }

    // Campaign goal
    campaignGoal: CampaignGoal | null;

    // Facebook Ad Account configuration
    fbAdAccount?: AdAccountPageType;

    // Facebook Pixel ID
    fbPixel?: FBPixel;

    // Facebook Page configuration
    fbPage?: FacebookPageType;

    // Instagram Page configuration
    igPage?: InstagramPageType;

    // Flag indicating if express setup is being used
    usesExpressSetup: boolean;

    // Call to action configuration
    cta: CallToActionType;

    // Selected creative assets for the campaign
    assets: CreativeSelectorPost[];

    // Links to promote
    linksToPromote: ExternalLink[];

    // for conversions campaigns, this is the platforms that are enabled 
    // on the link
    enabledPlatforms?: Array<{
        url: string;
        name: string;
    }>;

    // Campaign captions
    captions: Caption[];

    // Targeted geographies for the campaign
    targetedGeographies: TargetedGeographyBucket[];

    // Targeted audiences configuration
    targetedAudiences: TargetedAudience[];

    // Demographic targeting options
    targetedDemographics: {
        min_age: number;
        max_age: number;
        age_groups?: string[];
        languages: TargetedLanguage[];
        genders: TargetedGender[];
    };

    // Budget configuration for the campaign
    budget: number;
    currency: string;
    budgetType: 'daily' | 'lifetime';
    lengthConfig: CampaignBudgetDateConfig;

    // Conversions Settings
    conversionsEnabled: boolean;
    conversionsEventName?: string;

    // these are added by the backend automatically
    // after a campaign is created
    campaignName?: string;
    campaignImage?: string;
};

/**
 * Comprehensive type definition for TikTok campaign submissions
 * @typedef TikTokCampaignSubmissionType
 */
export type TikTokCampaignSubmissionType = {
    /** Type of playbook being used for the campaign */
    playbookType: PlaybookType;

    /** Music record details for streaming campaigns */
    record: RecordDetails | null;

    /** For Grow Playlist followers campaigns, this is the track that is being targeted */
    highlightedTrack?: {
        id: string,
        uri: string,
        name: string,
    }

    /** Campaign goal */
    campaignGoal: CampaignGoal | null;

    /** Facebook Ad Account configuration */
    tiktokAdAccount?: TikTokAdAccount;

    /** Flag indicating if express setup is being used */
    usesExpressSetup: boolean;

    /** Call to action configuration */
    cta: CallToActionType;

    /** Selected creative assets for the campaign */
    assets: CreativeSelectorPost[];
    selectedSparkCodes: TikTokSparkAd[];
    /** Links to promote */
    linksToPromote: ExternalLink[];

    /** for conversions campaigns, this is the platforms that are enabled 
     * on the link */
    enabledPlatforms?: Array<{
        url: string;
        name: string;
    }>;

    /** Campaign captions */
    captions: Caption[];

    /** Targeted geographies for the campaign */
    targetedGeographies: TargetedGeographyBucket[];

    /** Targeted audiences configuration */
    targetedAudiences: TargetedAudience[];

    /** Demographic targeting options */
    targetedDemographics: {
        min_age: number;
        max_age: number;
        age_groups?: string[];
        languages: TargetedLanguage[];
        genders: TargetedGender[];
    };

    /** Budget configuration for the campaign */
    budget: number;
    currency: string;
    budgetType: 'daily' | 'lifetime';
    lengthConfig: CampaignBudgetDateConfig;
    // these are added by the backend automatically
    // after a campaign is created
    campaignName?: string;
    campaignImage?: string;
};

/**
 * Comprehensive type definition for YouTube campaign submissions
 * @typedef YouTubeCampaignSubmissionType
 */
export type YouTubeCampaignSubmissionType = {
    /** Type of playbook being used for the campaign */
    playbookType: PlaybookType.INCREASE_VIDEO_VIEWS;
    campaignGoal: CampaignGoal;
    currency: string;

    video: {
        id: string;
        name: string;
        channelId: string;
        channelTitle: string;
        thumbnailUrl: string;
    }

    /** Targeted geographies for the campaign */
    targetedGeographies: Array<TargetedGeographyBucket>;

    /** Targeted audiences configuration */
    targetedAudiences: Array<TargetedAudience>;

    /** Budget configuration for the campaign */
    budget: number;
    budgetType: 'lifetime';
    lengthConfig: DateRangeConfig & { dateType: 'date_range' }

    // these are added by the backend automatically
    // after a campaign is created
    campaignName?: string;
    campaignImage?: string;
};

/** Type definition for call-to-action options in campaigns */
export type CallToActionType = {
    /** Unique identifier for the call-to-action */
    value: string;
    /** Display text for the call-to-action */
    label: string;
    /** Optional additional information about the call-to-action */
    note?: string;
};

export interface PlaybookTypeInfoWithPlaybookSteps extends PlaybookTypeInfo {
    entryPoint?: PlaybookStep;
    creativeMiddleContent?: React.ReactNode[];
    connectionsMiddleContent?: React.ReactNode[];
    allowExpressSetup?: boolean;
}

export const PROMOTABLE_ITEMS: PromotableItem[] = [
    PLAYBOOK_TYPE_INFO[PlaybookType.PRESAVE],

    // Meta
    {
        ...PLAYBOOK_TYPE_INFO[PlaybookType.INCREASE_MUSIC_STREAMS],
        isPopular: true,
    },
    PLAYBOOK_TYPE_INFO[PlaybookType.GROW_PLAYLIST_FOLLOWERS],
    PLAYBOOK_TYPE_INFO[PlaybookType.META_IG_SOUND],
    PLAYBOOK_TYPE_INFO[PlaybookType.META_EMAIL_SIGNUPS],
    PLAYBOOK_TYPE_INFO[PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT],
    PLAYBOOK_TYPE_INFO[PlaybookType.META_TICKET_SALES],
    {
        ...PLAYBOOK_TYPE_INFO[PlaybookType.META_PRESAVES],
        isPopular: true,
    },
    PLAYBOOK_TYPE_INFO[PlaybookType.INCREASE_LINK_CLICKS],

    // TikTok
    {
        ...PLAYBOOK_TYPE_INFO[PlaybookType.TIKTOK_BOOST_ENGAGEMENT],
        isPopular: true,
    },
    PLAYBOOK_TYPE_INFO[PlaybookType.TIKTOK_EXTERNAL_TRAFFIC],
    PLAYBOOK_TYPE_INFO[PlaybookType.TIKTOK_SOUND],
    PLAYBOOK_TYPE_INFO[PlaybookType.TIKTOK_MUSIC_STREAMS],

    // Youtube
    {
        ...PLAYBOOK_TYPE_INFO[PlaybookType.INCREASE_VIDEO_VIEWS],
        isPopular: false,
        unavailable: false,
    },

    // Playlists
    PLAYBOOK_TYPE_INFO[PlaybookType.GET_PLAYLISTED],
]

// ==================== DATA TRANSFORM FUNCTIONS ====================

export const transformDataForCampaignSubmit = (adPlatform: AdPlatform, campaignData: Partial<CampaignState>, connections?: FacebookCampaignConnections | {
    selectedTikTokAdAccount?: TikTokAdAccount;
}) => {
    switch (adPlatform) {
        case AdPlatform.META:
            return facebookPlaybook_to_campaignData(campaignData, connections as FacebookCampaignConnections);
        case AdPlatform.TIKTOK:
            return tiktokPlaybook_to_campaignData(campaignData, connections as {
                selectedTikTokAdAccount?: TikTokAdAccount;
            });
        case AdPlatform.YOUTUBE:
            return youtubePlaybook_to_campaignData(campaignData);
        case AdPlatform.PLAYLISTS:
            return getPlaylistedPlaybook_to_campaignData(campaignData);
        default:
            throw new Error(`Unsupported ad platform: ${adPlatform}`);
    }
}

const getPlaylistedPlaybook_to_campaignData = (campaignData: any) => {
    console.log('get playlisted playbook to campaign data');
    return campaignData;
}

const facebookPlaybook_to_campaignData = (state: Partial<CampaignState>, connections?: FacebookCampaignConnections): FacebookCampaignSubmissionType => {
    if (!state.playbookType) {
        throw new Error('Playbook type is required');
    }

    // Transform audience groups into targeted audiences format
    const targetedAudiences = state.selectedAudienceGroups?.map(group => ({
        id: group.id,
        name: group.title,
        audiences: group.tags.map(tag => ({
            id: String(tag.id),
            name: tag.title,
            audience_size: tag.audience_size,
            type: tag.type as "customAudience" | "lookalikeAudience" | "interestTargetingAudience" | "pageAudience" | "presetAudience"
        }))
    })) || [];

    // Transform demographics data
    const targetedDemographics = {
        min_age: state.minAge ?? 18,
        max_age: state.maxAge ?? 65,
        languages: state.languages || FB_CAMPAIGNS_DEFAULT_LANGUAGES,
        genders: state.genders || FB_DEFAULT_GENDERS,
    };

    // Determine budget configuration type and length
    const lengthConfig = state.currentDateSelectionTab === 'dateRange' ?
        {
            dateType: 'date_range' as const,
            startDate: state.startDate || new Date().toISOString(),
            endDate: state.endDate || new Date().toISOString()
        }
        :
        {
            dateType: 'duration' as const,
            startDate: state.startDate || new Date().toISOString(),
            numberOfDays: state.numberOfDays ?? 7
        };

    // Transform creative assets
    const assets = [
        ...transformPostsToCreativeFormat(state.selectedInstagramPosts || []),
        ...transformPostsToCreativeFormat(state.uploadedAssets || []),
        ...transformPostsToCreativeFormat(state.selectedSparkCodes || []),
        ...transformPostsToCreativeFormat(state.importedFromUrlPosts || []),
    ];

    let isExpressSetup = !(connections?.selectedFacebookPage && connections?.selectedFacebookAdAccount)

    return {
        playbookType: state.playbookType,
        record: state.selectedSongToPromote || null,
        fbAdAccount: connections?.selectedFacebookAdAccount,
        fbPixel: connections?.selectedFacebookPixel,
        fbPage: connections?.selectedFacebookPage,
        igPage: connections?.selectedInstagramPage,
        usesExpressSetup: isExpressSetup,
        cta: state.callToAction!,
        assets,
        campaignGoal: state.campaignGoal || null,
        linksToPromote: state.linksToPromote || [],
        highlightedTrack: state.highlightedTrack || undefined,
        enabledPlatforms: state.enabledPlatforms || [],
        captions: state.captions || [],
        targetedGeographies: state.geographyBuckets || [],
        targetedAudiences,
        targetedDemographics,
        budget: Number(state.budget || 0),
        currency: 'USD',
        budgetType: state.currentBudgetTab === 'daily' ? 'daily' : 'lifetime',
        lengthConfig,
        conversionsEnabled: state.conversionsEnabled || false,
        conversionsEventName: state.conversionsEventName || undefined,
    };
}

const tiktokPlaybook_to_campaignData = (state: Partial<CampaignState>, connections?: {
    selectedTikTokAdAccount?: TikTokAdAccount;
}): TikTokCampaignSubmissionType => {
    if (!state.playbookType) {
        throw new Error('Playbook type is required');
    }

    // Transform audience groups into targeted audiences format
    const targetedAudiences = state.selectedAudienceGroups?.map(group => ({
        id: group.id,
        name: group.title,
        audiences: group.tags.map(tag => ({
            id: String(tag.id),
            name: tag.title,
            audience_size: tag.audience_size,
            type: tag.type as "customAudience" | "lookalikeAudience" | "interestTargetingAudience" | "pageAudience" | "presetAudience"
        }))
    })) || [];

    // Transform demographics data
    const targetedDemographics = {
        min_age: state.minAge ?? 18,
        max_age: state.maxAge ?? 65,
        languages: state.languages || FB_CAMPAIGNS_DEFAULT_LANGUAGES,
        genders: state.genders || FB_DEFAULT_GENDERS,
    };

    // Determine budget configuration type and length
    const lengthConfig = state.currentDateSelectionTab === 'dateRange' ?
        {
            dateType: 'date_range' as const,
            startDate: state.startDate || new Date().toISOString(),
            endDate: state.endDate || new Date().toISOString()
        }
        :
        {
            dateType: 'duration' as const,
            startDate: state.startDate || new Date().toISOString(),
            numberOfDays: state.numberOfDays ?? 7
        };

    // Transform creative assets
    const assets = [
        ...transformPostsToCreativeFormat(state.selectedSparkCodes || []),
    ];

    return {
        selectedSparkCodes: state.selectedSparkCodes || [],
        playbookType: state.playbookType,
        record: state.selectedSongToPromote || null,
        tiktokAdAccount: connections?.selectedTikTokAdAccount,
        usesExpressSetup: true,
        cta: state.callToAction!,
        assets,
        campaignGoal: state.campaignGoal || null,
        linksToPromote: state.linksToPromote || [],
        highlightedTrack: state.highlightedTrack || undefined,
        enabledPlatforms: state.enabledPlatforms || [],
        captions: state.captions || [],
        targetedGeographies: state.geographyBuckets || [],
        targetedAudiences,
        targetedDemographics,
        budget: Number(state.budget || 0),
        currency: 'USD',
        budgetType: state.currentBudgetTab === 'daily' ? 'daily' : 'lifetime',
        lengthConfig,
    };
}

const youtubePlaybook_to_campaignData = (campaignData: any) => {
    console.log('increase video views to campaign data');
    return campaignData;
}

export const getGenderText = (gender: TargetedGender) => {
    if (gender.name === GENDERS.MALE) {
        return 'Male';
    }

    if (gender.name === GENDERS.FEMALE) {
        return 'Female';
    }

    return 'All';
}